import React, { useCallback, useMemo, useState } from 'react';
import { useKlpStyles } from '@klp/design/Theme/useKlpStyles';
import {
    FormControl,
    FormControlLabel,
    FormLabel,
    RadioGroup,
    Radio,
    Paper,
} from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { LocalLoader } from '@klp/design/Loader';
import MUIDataTable from 'mui-datatables';
import type { MUIDataTableColumn, MUIDataTableOptions } from 'mui-datatables';
import { Heading } from '@klp/design/Heading';
import { useMappingTableData } from '../../hooks/useMappingTableData';
import classNames from 'classnames';
import { getStyles, getTableColumns, getTableOptions } from './tableConfigs';

type MappingFilter = 'all' | 'mapped' | 'unmapped';

const styles = getStyles();

export const MappingTable = () => {
    const { t } = useTranslation();
    const classes = useKlpStyles({}, styles);
    const { filter, setFilter, flatList, filteredData, isLoading, isFetching } =
        useMappingTableData();
    const [openPopperId, setOpenPopperId] = useState<string | null>(null);
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

    const handleTogglePopper = useCallback(
        (reportId: string) => (event: React.MouseEvent<HTMLButtonElement>) => {
            if (openPopperId === reportId) {
                setOpenPopperId(null);
                setAnchorEl(null);
            } else {
                setOpenPopperId(reportId);
                setAnchorEl(event.currentTarget);
            }
        },
        [openPopperId],
    );

    const handleClosePopper = useCallback(() => {
        setOpenPopperId(null);
        setAnchorEl(null);
    }, []);

    const filteredDataMap = useMemo(
        () => new Map(filteredData.map((item) => [item.id, item])),
        [filteredData],
    );

    const tableColumns: Array<MUIDataTableColumn> = useMemo(
        () =>
            getTableColumns({
                filteredDataMap,
                openPopperId,
                anchorEl,
                handleTogglePopper,
                handleClosePopper,
            }),
        [
            filteredDataMap,
            openPopperId,
            anchorEl,
            handleTogglePopper,
            handleClosePopper,
        ],
    );

    const tableOptions: MUIDataTableOptions = useMemo(
        () => getTableOptions({ openPopperId, flatList }),
        [flatList, openPopperId],
    );

    const tableTitle = (
        <Heading
            klpHeadingLevel="THREE"
            klpColor="dark"
            translation="reportMapping.menu.title"
        />
    );

    return (
        <LocalLoader isLoading={isLoading || isFetching}>
            <Paper elevation={0} className={classes.filterContainer}>
                <FormControl component="fieldset">
                    <FormLabel component="legend">
                        {t('reportMapping.mappingTable.filterByStatus')}
                    </FormLabel>
                    <RadioGroup
                        row={true}
                        value={filter}
                        onChange={(event) =>
                            setFilter(event.target.value as MappingFilter)
                        }
                    >
                        <FormControlLabel
                            value="all"
                            control={<Radio />}
                            label={t('reportMapping.mappingTable.all')}
                        />
                        <FormControlLabel
                            value="mapped"
                            control={<Radio />}
                            label={t('reportMapping.mappingTable.mapped')}
                        />
                        <FormControlLabel
                            value="unmapped"
                            control={<Radio />}
                            label={t('reportMapping.mappingTable.unmapped')}
                        />
                    </RadioGroup>
                </FormControl>
            </Paper>
            <div
                className={classNames(classes.table, {
                    [classes.tableBlurred]: openPopperId,
                })}
            >
                <MUIDataTable
                    title={tableTitle}
                    data={flatList}
                    columns={tableColumns}
                    options={tableOptions}
                />
            </div>
        </LocalLoader>
    );
};
